/**
 * Booking form — slot catalogue (V2 engine).
 *
 * Slots are composite blocks the registry can't express with a single
 * field — multiple inputs working together, conditional rendering,
 * external side-effects, or cross-tab dependencies (cold-chain → temp
 * block visibility).
 *
 * The engine concatenates `FIELDS[tab=current].sort(position)` with
 * `SLOTS[tab=current].sort(position)` and renders them in one
 * position-ordered stream per tab.
 *
 * Each slot here is a `{ key, tab, position, component }` triple. The
 * component is a React component that receives `{ values, set, setValues,
 * picks, ctx }`. `ctx` is a grab-bag of tab-scoped extras populated by
 * useBookingForm (sites, bays, selectedSite, selectedBay, etc.).
 *
 * `fullWidth: true` means the slot escapes the 2-col grid and spans the row.
 */
import ScheduleHeaderSlot from "../slots/ScheduleHeaderSlot";
import VehicleMassHeadingSlot from "../slots/VehicleMassHeadingSlot";
import RestraintCountsHeadingSlot from "../slots/RestraintCountsHeadingSlot";
import RestraintFlagsHeadingSlot from "../slots/RestraintFlagsHeadingSlot";
import RestraintPalletsHeadingSlot from "../slots/RestraintPalletsHeadingSlot";
import ColdChainBlockSlot from "../slots/ColdChainBlockSlot";
import PicklistLayoutSlot from "../slots/PicklistLayoutSlot";
import DocAISlot from "../slots/DocAISlot";
import DeclarationsSlot from "../slots/DeclarationsSlot";
import DriverPickerSlot from "../slots/DriverPickerSlot";
import QAConfirmCardsSlot from "../slots/QAConfirmCardsSlot";
import FldOnlyBlockSlot from "../slots/FldOnlyBlockSlot";
import TrailerWeightsSlot from "../slots/TrailerWeightsSlot";
import TemperaturesBlockSlot from "../slots/TemperaturesBlockSlot";
import DamageBlockSlot from "../slots/DamageBlockSlot";
import PalletFlowBlockSlot from "../slots/PalletFlowBlockSlot";
import SamplingIssuesSlot from "../slots/SamplingIssuesSlot";

export const SLOTS = [
  // ── DOCS ──────────────────────────────────────────────────────────
  { key: "docs.docai", tab: "docs", position: 50,
    component: DocAISlot, fullWidth: true, label: "Document AI parser" },

  // ── SCHEDULE ──────────────────────────────────────────────────────
  // Header owns site/bay/direction/reservation/date/start/end + bay-note
  // + out-of-hours warning + site-hours info row. Position 0 = top of tab.
  { key: "schedule.header", tab: "schedule", position: 0,
    component: ScheduleHeaderSlot, fullWidth: true,
    label: "Schedule header", essential: true },

  // ── VEHICLE ───────────────────────────────────────────────────────
  // Picklist categories (vehicle_type etc.) merged from lbs_picklist_field_layout
  { key: "vehicle.picklists", tab: "vehicle", position: 50,
    component: PicklistLayoutSlot, fullWidth: true,
    label: "Vehicle picklists", essential: true },
  // "Mass limits (kg)" heading sits above the GVM/steer/drive trio
  { key: "vehicle.mass-heading", tab: "vehicle", position: 200,
    component: VehicleMassHeadingSlot, fullWidth: true,
    label: "Mass-limits heading" },

  // ── DRIVER ────────────────────────────────────────────────────────
  { key: "driver.picker", tab: "driver", position: 0,
    component: DriverPickerSlot, fullWidth: true,
    label: "Driver picker", essential: true },
  { key: "driver.declarations", tab: "driver", position: 500,
    component: DeclarationsSlot, fullWidth: true,
    label: "Driver declarations" },

  // ── RESTRAINT ─────────────────────────────────────────────────────
  { key: "restraint.picklists", tab: "restraint", position: 50,
    component: PicklistLayoutSlot, fullWidth: true,
    label: "Restraint picklists", essential: true },
  { key: "restraint.counts-heading", tab: "restraint", position: 200,
    component: RestraintCountsHeadingSlot, fullWidth: true,
    label: "Restraint-counts heading" },
  { key: "restraint.flags-heading", tab: "restraint", position: 300,
    component: RestraintFlagsHeadingSlot, fullWidth: true,
    label: "Restraint-flags heading" },
  { key: "restraint.pallets-heading", tab: "restraint", position: 400,
    component: RestraintPalletsHeadingSlot, fullWidth: true,
    label: "Pallets heading" },

  // ── COMPLIANCE ────────────────────────────────────────────────────
  // Cold-chain Y/N + chill/frozen sub-choice (single source of truth)
  { key: "compliance.cold-chain", tab: "compliance", position: 50,
    component: ColdChainBlockSlot, fullWidth: true,
    label: "Cold-chain Y/N" },
  // Picklist categories (excluding cold_chain — handled above)
  { key: "compliance.picklists", tab: "compliance", position: 100,
    component: PicklistLayoutSlot, fullWidth: true,
    componentProps: { exclude: ["cold_chain"] },
    label: "Compliance picklists", essential: true },

  // ── QA (Site Checks) — all FLD-only composite blocks ──────────────
  { key: "qa.confirm-cards", tab: "qa", position: 10,
    component: QAConfirmCardsSlot, fullWidth: true,
    label: "QA confirm cards (6 tabs)" },
  { key: "qa.fld-only", tab: "qa", position: 100,
    component: FldOnlyBlockSlot, fullWidth: true,
    label: "FLD-only block" },
  { key: "qa.trailer-weights", tab: "qa", position: 200,
    component: TrailerWeightsSlot, fullWidth: true,
    label: "Trailer weights" },
  { key: "qa.temperatures", tab: "qa", position: 300,
    component: TemperaturesBlockSlot, fullWidth: true,
    label: "Temperatures + contamination + trailer-clean" },
  { key: "qa.damage", tab: "qa", position: 400,
    component: DamageBlockSlot, fullWidth: true,
    label: "Damage block" },
  { key: "qa.pallet-flow", tab: "qa", position: 500,
    component: PalletFlowBlockSlot, fullWidth: true,
    label: "Pallet flow (actuals + account)" },
  { key: "qa.sampling-issues", tab: "qa", position: 900,
    component: SamplingIssuesSlot, fullWidth: true,
    label: "Sampling + Intake Form + issues panel" },
];

export function slotsForTab(tabKey) {
  return SLOTS
    .filter((s) => s.tab === tabKey)
    .slice()
    .sort((a, b) => (a.position ?? 0) - (b.position ?? 0));
}
